import React, { useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { useAccount } from '../../context/AccountContext';
import { InkSkeleton } from '../cms/InkSkeleton';
import { neverApproved, phaseOf } from './campaignPhase';
import { addDays, calendarDate, campaignRange, channelMark, channelName, postTime } from './reach';
import {
  Board,
  BoardCard,
  BoardContainer,
  CAMPAIGN_BOARD,
  CAMPAIGN_LIST,
  CampaignSummary,
  campaignTone,
  dueDay,
  dueWords,
  firstName,
  monthDay,
} from './campaignCards';
import { campaignHue, campaignPaint } from './campaignColors';

/**
 * Campaigns → Calendar (TinyCMS UX optimization review, 2026-10-23): every
 * campaign as a bar from its start to its end, over 8 weeks or 6 months.
 * Opening one shows one row per channel (its board's containers): a dot
 * for a post on the day it goes out (amber while it waits for approval), a
 * diamond for the landing page going live (the campaign's start), a square
 * for anything else on its due date, green once done. Clicking a campaign
 * opens its board; clicking a mark opens that card there.
 */

type Range = '8w' | '6m';
const MO = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * 8 weeks from this week's Monday; 6 months from the start of the month three
 * weeks back, so the view looks ahead with a little recent work in sight
 * (backlog 5ed9d4bf), in whole months, each column named by its month (QA
 * round 23: "Oct 5 · Nov · … · Ap", the last one four days wide).
 */
function windowFor(range: Range, now = new Date()) {
  const from = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  from.setDate(from.getDate() - ((from.getDay() + 6) % 7));
  if (range === '8w') {
    const to = new Date(from);
    to.setDate(to.getDate() + 8 * 7);
    const ticks = Array.from({ length: 8 }, (_, i) => {
      const d = new Date(from);
      d.setDate(d.getDate() + i * 7);
      return { at: d, label: monthDay(d) };
    });
    const last = addDays(to, -1);
    return { from, to, ticks, note: `${monthDay(from)} – ${monthDay(last)}` };
  }
  from.setDate(from.getDate() - 3 * 7);
  const first = new Date(from.getFullYear(), from.getMonth(), 1);
  const to = new Date(first.getFullYear(), first.getMonth() + 6, 1);
  const ticks = Array.from({ length: 6 }, (_, i) => {
    const d = new Date(first.getFullYear(), first.getMonth() + i, 1);
    return { at: d, label: MO[d.getMonth()] + (d.getMonth() === 0 ? ` ${d.getFullYear()}` : '') };
  });
  const last = addDays(to, -1);
  return {
    from: first,
    to,
    ticks,
    note: `${MO[first.getMonth()]} – ${MO[last.getMonth()]} ${last.getFullYear()}`,
  };
}

const PHASE_ORDER = { running: 0, upcoming: 1, ended: 2 } as const;

export function CampaignCalendar() {
  const navigate = useNavigate();
  const { accountId, selectedAccount } = useAccount();
  const campaignRole = Boolean(selectedAccount?.campaignRole);
  const approver = selectedAccount?.approverName ? firstName(selectedAccount.approverName) : null;
  const [range, setRange] = useState<Range>('8w');
  const [open, setOpen] = useState<Record<string, boolean> | null>(null);
  const { data, loading, error, refetch } = useQuery<{ campaignList: CampaignSummary[] }>(
    CAMPAIGN_LIST,
    { variables: { accountId }, skip: !accountId, fetchPolicy: 'cache-and-network' }
  );
  const now = new Date();
  const win = windowFor(range, now);
  const span = win.to.getTime() - win.from.getTime();
  const pct = (t: number) => ((t - win.from.getTime()) / span) * 100;
  const clampPct = (t: number) => `${Math.max(0, Math.min(100, pct(t))).toFixed(2)}%`;
  const todayAt = clampPct(now.getTime());

  const rows = useMemo(() => {
    const list = data?.campaignList || [];
    return list
      .map((c) => {
        const phase = phaseOf(c, now);
        const waiting = phase !== 'ended' && c.posts.some((p) => p.status === 'in_review');
        const tone = campaignTone(
          phase,
          c,
          { waiting, canApprove: !campaignRole, approver },
          neverApproved(c)
        );
        const start = calendarDate(c.startDate) || (c.createdAt ? new Date(c.createdAt) : null);
        const end = calendarDate(c.deadline);
        if (end) end.setHours(23, 59, 59, 999);
        // The bar in the campaign's color, showing its status (campaignColors.ts).
        const paint = campaignPaint(campaignHue(c), tone.state);
        return { c, phase, tone, start, end, paint };
      })
      .sort(
        (a, b) =>
          PHASE_ORDER[a.phase] - PHASE_ORDER[b.phase] ||
          (b.start?.getTime() || 0) - (a.start?.getTime() || 0)
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, campaignRole, approver]);

  // The first campaign that isn't over starts open.
  const isOpen = (id: string) =>
    open ? Boolean(open[id]) : rows.find((r) => r.phase !== 'ended')?.c.id === id;
  const toggle = (id: string) =>
    setOpen((o) => ({
      ...(o || Object.fromEntries(rows.map((r) => [r.c.id, isOpen(r.c.id)]))),
      [id]: !isOpen(id),
    }));

  const goCampaign = (id: string, card?: string) =>
    navigate(
      `/campaigns?campaign=${encodeURIComponent(id)}${card ? `&card=${encodeURIComponent(card)}` : ''}`
    );

  return (
    <div className="ccal">
      <div className="ccal-top">
        <span className="ink-seg ccal-range">
          {(
            [
              ['8w', '8 weeks'],
              ['6m', '6 months'],
            ] as Array<[Range, string]>
          ).map(([k, label]) => (
            <button
              key={k}
              type="button"
              data-active={range === k ? 'true' : 'false'}
              onClick={() => setRange(k)}
            >
              {label}
            </button>
          ))}
        </span>
        <span className="ccal-legend">
          <span>
            <i className="ccal-mk" data-shape="post" />
            Post goes out
          </span>
          <span>
            <i className="ccal-mk" data-shape="post" data-tone="wait" />
            Post waiting on approval
          </span>
          <span>
            <i className="ccal-mk" data-shape="lp" />
            Page goes live
          </span>
          <span>
            <i className="ccal-mk" data-shape="due" />
            Due date
          </span>
          <span>
            <i className="ccal-mk" data-shape="post" data-tone="done" />
            Done
          </span>
        </span>
      </div>

      {loading && !data ? (
        <InkSkeleton rows={4} label="Loading campaigns" style={{ marginTop: 16 }} />
      ) : error && !data ? (
        <p className="ink-muted" role="alert" style={{ marginTop: 16 }}>
          Campaigns couldn’t load just now.{' '}
          <button type="button" className="ink-quiet-link" onClick={() => void refetch()}>
            Try again
          </button>
        </p>
      ) : !rows.length ? (
        <p className="ink-muted" style={{ marginTop: 16 }}>
          No campaigns yet. Start one under Campaigns.
        </p>
      ) : (
        <>
          <div className="ccal-scroll">
            <div className="ccal-grid">
              <div className="ccal-row ccal-head">
                <span className="ccal-label ccal-range-note">{win.note}</span>
                <div className="ccal-track">
                  {win.ticks.map((t) => (
                    <span
                      key={t.label}
                      className="ccal-tick"
                      style={{ left: clampPct(t.at.getTime()) }}
                    >
                      {t.label}
                    </span>
                  ))}
                  {/* Under the week labels, on the Today line, so the first label stays
                      readable (backlog d884075c). */}
                  <i className="ccal-now ccal-now-head" style={{ left: todayAt }} />
                  <span className="ccal-today-pill" style={{ left: todayAt }}>
                    Today
                  </span>
                </div>
              </div>
              {rows.map(({ c, tone, start, end, paint }) => {
                const inView = Boolean(
                  start && end && end >= win.from && start.getTime() <= win.to.getTime()
                );
                const opened = isOpen(c.id);
                return (
                  <div className="ccal-camp" key={c.id}>
                    <div className="ccal-row">
                      <button
                        type="button"
                        className="ccal-label ccal-name"
                        aria-expanded={opened}
                        onClick={() => toggle(c.id)}
                      >
                        <span className="ccal-caret" aria-hidden="true">
                          {opened ? '▾' : '▸'}
                        </span>
                        <span style={{ minWidth: 0 }}>
                          <span className="ccal-title">
                            <i className="ccp-dot" style={{ background: paint.chip }} />
                            {c.name}
                          </span>
                          <span className="ccal-status" style={{ color: tone.ink }}>
                            <span className="cb-dot" style={{ background: tone.dot }} />
                            {tone.label}
                          </span>
                        </span>
                      </button>
                      <div className="ccal-track">
                        <i className="ccal-now" style={{ left: todayAt }} />
                        {inView && start && end ? (
                          (() => {
                            // Begun before the range or running past it: the bar
                            // is cut square on that side, with an arrow (QA round 23).
                            const before = pct(start.getTime()) < 0;
                            const after = pct(end.getTime()) > 100;
                            const from = Math.max(0, pct(start.getTime()));
                            const until = Math.min(100, pct(end.getTime()));
                            return (
                              <button
                                type="button"
                                className="ccal-bar"
                                data-before={before || undefined}
                                data-after={after || undefined}
                                title={`${campaignRange(c)}${
                                  before ? ' · began before this range' : ''
                                }${after ? ' · runs past this range' : ''}`}
                                style={{
                                  left: `${from.toFixed(2)}%`,
                                  width: `${Math.max(1.5, until - from).toFixed(2)}%`,
                                  background: paint.background,
                                  border: paint.border,
                                  color: paint.ink,
                                }}
                                onClick={() => goCampaign(c.id)}
                              >
                                {before ? <span aria-hidden="true">←&nbsp;</span> : null}
                                {campaignRange(c)}
                                {after ? <span aria-hidden="true">&nbsp;→</span> : null}
                              </button>
                            );
                          })()
                        ) : (
                          <span className="ccal-out">{campaignRange(c)} · outside this range</span>
                        )}
                      </div>
                    </div>
                    {opened ? (
                      <CampaignLanes
                        campaign={c}
                        start={start}
                        pct={pct}
                        todayAt={todayAt}
                        onOpen={(card) => goCampaign(c.id, card)}
                      />
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
          <p className="ccal-foot">
            Click a campaign to see one row per channel. Hover a mark for what it is; click it to
            open it.
          </p>
        </>
      )}
    </div>
  );
}

/** Where on the timeline a card sits, and its mark. */
function placeCard(card: BoardCard, start: Date | null) {
  const post = card.type === 'post' ? card.post : null;
  const postAt = post ? postTime(post) : null;
  if (post && postAt) {
    return {
      at: postAt,
      shape: 'post',
      tone: card.done ? 'done' : post.reachStatus === 'waiting' ? 'wait' : undefined,
      verb: post.reachStatus === 'posted' ? 'went out' : 'goes out',
    };
  }
  if (card.type === 'plan' && card.plan?.scheduledDate) {
    return {
      at: new Date(card.plan.scheduledDate),
      shape: 'post',
      tone: card.done ? 'done' : card.status === 'waiting' ? 'wait' : undefined,
      verb: 'goes out',
    };
  }
  if (card.type === 'landing' && start) {
    return { at: start, shape: 'lp', tone: card.done ? 'done' : undefined, verb: 'goes live' };
  }
  const due = dueDay(card);
  if (!due) return null;
  return { at: due, shape: 'due', tone: card.done ? 'done' : undefined, verb: 'due', due };
}

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function CampaignLanes({
  campaign,
  start,
  pct,
  todayAt,
  onOpen,
}: {
  campaign: CampaignSummary;
  start: Date | null;
  pct: (t: number) => number;
  todayAt: string;
  onOpen: (cardKey: string) => void;
}) {
  const { data, loading } = useQuery<{ campaignBoard: Board }>(CAMPAIGN_BOARD, {
    variables: { campaignId: campaign.id },
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });
  const board = data?.campaignBoard;
  if (!board) {
    return (
      <div className="ccal-row ccal-lane">
        <span className="ccal-label ccal-lane-name ink-muted">
          {loading ? 'Loading its work…' : 'Its work couldn’t load.'}
        </span>
        <div className="ccal-track" />
      </div>
    );
  }
  const laneName = (col: BoardContainer) =>
    col.type === 'website'
      ? 'Website'
      : col.type === 'other'
        ? 'Other work'
        : channelName(col.platform || '');
  const badge = (col: BoardContainer) =>
    col.type === 'website' ? '▤' : col.type === 'other' ? '✓' : channelMark(col.platform || '');
  return (
    <>
      {board.containers
        .filter((col) => col.cards.length || col.type === 'website')
        .map((col) => {
          const marks = col.cards
            .map((card) => ({ card, place: placeCard(card, start) }))
            .filter(
              (m) => m.place && pct(m.place.at.getTime()) >= 0 && pct(m.place.at.getTime()) <= 100
            );
          return (
            <div className="ccal-row ccal-lane" key={col.key}>
              <span className="ccal-label ccal-lane-name">
                <span className="cb-col-badge ccal-badge">{badge(col)}</span>
                <span className="ccal-lane-text">{laneName(col)}</span>
                <span className="ccal-count">{col.cards.length}</span>
              </span>
              <div className="ccal-track">
                <i className="ccal-now" data-faint="true" style={{ left: todayAt }} />
                {marks.map(({ card, place }) => {
                  const p = place!;
                  const d = p.at;
                  // A ready card met its due date: no overdue red (backlog b35b62cf).
                  const ready = card.done || card.dueMet || card.fields.every((f) => f.ok);
                  const dueInk =
                    p.shape === 'due' && !p.tone && !ready
                      ? dueWords(p.due || null).ink
                      : undefined;
                  const tip = `${card.kind} · ${card.text} · ${p.verb} ${DOW[d.getDay()]} ${monthDay(d)}${
                    !card.done && card.fields.every((f) => f.ok) ? ' · ready' : ''
                  }`;
                  return (
                    <button
                      key={card.key}
                      type="button"
                      className="ccal-mk ccal-mark"
                      data-shape={p.shape}
                      data-tone={p.tone}
                      title={tip}
                      aria-label={tip}
                      style={{
                        left: `${pct(d.getTime()).toFixed(2)}%`,
                        ...(dueInk ? { borderColor: dueInk } : {}),
                      }}
                      onClick={() => onOpen(card.key)}
                    />
                  );
                })}
              </div>
            </div>
          );
        })}
    </>
  );
}
